import { AuthError, appPath, createHiveClient, createTenantAuth, isApiError, unwrap, withBase, type AuthAnswer, type Me, type Problem, type SignIn, type TenantAuth } from '@hive/api';
import { I18nProvider, resolveLang, type Lang } from '@hive/i18n';
import { applyTheme, ErrorBox, HelpProvider, Loading, Note, TagSuggestContext, ToastProvider, explainProblem, type TagSuggestion } from '@hive/ui';
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { loadPlatformMessages, platformMessages, useI18nP, useT, type PlatformKey } from './i18n';
import { createHelpSource } from './lib/help-source';
import { wizardRoute } from './lib/routes';
import { applyBranding, rememberedLang, rememberLang, SessionProvider, themeOf, useSession } from './lib/session';
import { currentSlug } from './lib/tenant';
import { Home } from './screens/home/Home';
import { NotFound } from './screens/NotFound';
import { LicenseBanner, prefetchLicense } from './screens/settings/LicenseBanner';
import { SignInScreen } from './screens/signin/SignInScreen';
import { Shell } from './shell/Shell';
import { apiBase } from './lib/apiBase';

// Telas carregadas sob demanda: a primeira carga fica leve no celular (RNF-02a).
const AuditScreen = lazy(() => import('./screens/audit/AuditScreen').then((m) => ({ default: m.AuditScreen })));
const HelpCenter = lazy(() => import('./screens/help/HelpCenter').then((m) => ({ default: m.HelpCenter })));
const ConnectScreen = lazy(() => import('./screens/integrations/ConnectScreen').then((m) => ({ default: m.ConnectScreen })));
const ConnectionDetail = lazy(() => import('./screens/integrations/ConnectionDetail').then((m) => ({ default: m.ConnectionDetail })));
const IntegrationsScreen = lazy(() => import('./screens/integrations/IntegrationsScreen').then((m) => ({ default: m.IntegrationsScreen })));
const SecurityConnectorsScreen = lazy(() => import('./screens/integrations/SecurityConnectorsScreen').then((m) => ({ default: m.SecurityConnectorsScreen })));
const ApiDocsScreen = lazy(() => import('./screens/integrations/ApiDocsScreen').then((m) => ({ default: m.ApiDocsScreen })));
const SettingsScreen = lazy(() => import('./screens/settings/SettingsScreen').then((m) => ({ default: m.SettingsScreen })));
const LicenseScreen = lazy(() => import('./screens/settings/LicenseScreen').then((m) => ({ default: m.LicenseScreen })));
const InstallationHealthScreen = lazy(() => import('./screens/settings/InstallationHealth').then((m) => ({ default: m.InstallationHealthScreen })));
const ProfileEditor = lazy(() => import('./screens/users/ProfileEditor').then((m) => ({ default: m.ProfileEditor })));
const UsersScreen = lazy(() => import('./screens/users/UsersScreen').then((m) => ({ default: m.UsersScreen })));
const AssetsScreen = lazy(() => import('./screens/inventory/AssetsScreen').then((m) => ({ default: m.AssetsScreen })));
const CustomFieldsScreen = lazy(() => import('./screens/inventory/FieldsScreens').then((m) => ({ default: m.CustomFieldsScreen })));
const SecurityOverview = lazy(() => import('./screens/inventory/SecurityOverview').then((m) => ({ default: m.SecurityOverview })));
const VulnScreen = lazy(() => import('./screens/vulns/VulnScreen').then((m) => ({ default: m.VulnScreen })));
const CveDetail = lazy(() => import('./screens/vulns/CveDetail').then((m) => ({ default: m.CveDetail })));
const RiskSettings = lazy(() => import('./screens/vulns/RiskSettings').then((m) => ({ default: m.RiskSettings })));
const ProductOverview = lazy(() => import('./screens/licenses/M365').then((m) => ({ default: m.ProductOverview })));
const LicensesScreen = lazy(() => import('./screens/licenses/LicensesScreen').then((m) => ({ default: m.LicensesScreen })));
const ContractsScreen = lazy(() => import('./screens/contracts/ContractsScreen').then((m) => ({ default: m.ContractsScreen })));
const ContractWizard = lazy(() => import('./screens/contracts/ContractWizard').then((m) => ({ default: m.ContractWizard })));
const KnownErrorsScreen = lazy(() => import('./screens/alerts/KnownErrorsScreen').then((m) => ({ default: m.KnownErrorsScreen })));
const ExperienceScreen = lazy(() => import('./screens/experience/ExperienceScreen').then((m) => ({ default: m.ExperienceScreen })));
const ComplianceScreen = lazy(() => import('./screens/config/ComplianceScreen').then((m) => ({ default: m.ComplianceScreen })));
const OrgScreen = lazy(() => import('./screens/org/OrgScreen').then((m) => ({ default: m.OrgScreen })));
const AttackPathsScreen = lazy(() => import('./screens/attackpaths/AttackPathsScreen').then((m) => ({ default: m.AttackPathsScreen })));
const ServiceMapScreen = lazy(() => import('./screens/servicemap/ServiceMapScreen').then((m) => ({ default: m.ServiceMapScreen })));
const CloudScreen = lazy(() => import('./screens/cloud/CloudScreen').then((m) => ({ default: m.CloudScreen })));
const CloudPostureScreen = lazy(() => import('./screens/cloud/CloudPostureScreen').then((m) => ({ default: m.CloudPostureScreen })));
const SnmpTrapsScreen = lazy(() => import('./screens/snmptraps/SnmpTrapsScreen').then((m) => ({ default: m.SnmpTrapsScreen })));
const RightsizingScreen = lazy(() => import('./screens/rightsizing/RightsizingScreen').then((m) => ({ default: m.RightsizingScreen })));
const OtScreen = lazy(() => import('./screens/ot/OtScreen').then((m) => ({ default: m.OtScreen })));
const VirtualizationScreen = lazy(() => import('./screens/virt/VirtualizationScreen').then((m) => ({ default: m.VirtualizationScreen })));
const IdentitiesScreen = lazy(() => import('./screens/identity/IdentitiesScreen').then((m) => ({ default: m.IdentitiesScreen })));
const AccessReviewsScreen = lazy(() => import('./screens/identity/AccessReviewsScreen').then((m) => ({ default: m.AccessReviewsScreen })));
const SaasScreen = lazy(() => import('./screens/identity/SaasScreen').then((m) => ({ default: m.SaasScreen })));
const KubernetesScreen = lazy(() => import('./screens/k8s/KubernetesScreen').then((m) => ({ default: m.KubernetesScreen })));
const ApisScreen = lazy(() => import('./screens/apis/ApisScreen').then((m) => ({ default: m.ApisScreen })));
const ApiScreen = lazy(() => import('./screens/apis/ApisScreen').then((m) => ({ default: m.ApiScreen })));
const ClusterScreen = lazy(() => import('./screens/k8s/KubernetesScreen').then((m) => ({ default: m.ClusterScreen })));
const GraphScreen = lazy(() => import('./screens/graph/GraphScreen').then((m) => ({ default: m.GraphScreen })));
const GeoScreen = lazy(() => import('./screens/geo/GeoScreen').then((m) => ({ default: m.GeoScreen })));
const FirewallsWifiScreen = lazy(() => import('./screens/network/NetConfigScreens').then((m) => ({ default: m.FirewallsWifiScreen })));
const ConfigBackupsScreen = lazy(() => import('./screens/network/NetConfigScreens').then((m) => ({ default: m.ConfigBackupsScreen })));
const CodeScreen = lazy(() => import('./screens/surface/CodeScreen').then((m) => ({ default: m.CodeScreen })));
const SurfaceScreen = lazy(() => import('./screens/surface/SurfaceScreen').then((m) => ({ default: m.SurfaceScreen })));
const NetworkScreen = lazy(() => import('./screens/network/NetworkScreen').then((m) => ({ default: m.NetworkScreen })));
const PrivilegesScreen = lazy(() => import('./screens/config/Privileges').then((m) => ({ default: m.PrivilegesScreen })));
const DecisionsScreen = lazy(() => import('./screens/ops/DecisionsScreen').then((m) => ({ default: m.DecisionsScreen })));
const AppControlScreen = lazy(() => import('./screens/config/AppControlScreen').then((m) => ({ default: m.AppControlScreen })));
const IocScreen = lazy(() => import('./screens/vulns/IocScreen').then((m) => ({ default: m.IocScreen })));
const PerformanceScreen = lazy(() => import('./screens/perf/PerformanceScreen').then((m) => ({ default: m.PerformanceScreen })));
// Roadmap 02/2027: menu Medição de rede
const nmScreens = () => import('./screens/netmeasure/NetMeasureScreens');
const NetOverviewScreen = lazy(() => nmScreens().then((m) => ({ default: m.NetOverviewScreen })));
const NetLinksScreen = lazy(() => nmScreens().then((m) => ({ default: m.NetLinksScreen })));
const NetTransactionsScreen = lazy(() => nmScreens().then((m) => ({ default: m.NetTransactionsScreen })));
const NetFlowsScreen = lazy(() => nmScreens().then((m) => ({ default: m.NetFlowsScreen })));
const NetConfigScreen = lazy(() => nmScreens().then((m) => ({ default: m.NetConfigScreen })));
const NetInvestigationsScreen = lazy(() => import('./screens/netmeasure/NetInvestigationsScreen').then((m) => ({ default: m.NetInvestigationsScreen })));
const NetCallsScreen = lazy(() => import('./screens/netmeasure/NetCallsScreen').then((m) => ({ default: m.NetCallsScreen })));
const NetRumScreen = lazy(() => import('./screens/netmeasure/NetRumScreen').then((m) => ({ default: m.NetRumScreen })));
const NetPacketsScreen = lazy(() => import('./screens/netmeasure/NetPacketsScreen').then((m) => ({ default: m.NetPacketsScreen })));
const NetSyntheticScreen = lazy(() => import('./screens/netmeasure/NetSyntheticScreen').then((m) => ({ default: m.NetSyntheticScreen })));
const NetAppExpScreen = lazy(() => import('./screens/netmeasure/NetAppExpScreen').then((m) => ({ default: m.NetAppExpScreen })));
const NetAccelScreen = lazy(() => import('./screens/netmeasure/NetAccelScreen').then((m) => ({ default: m.NetAccelScreen })));
const NetPathsScreen = lazy(() => import('./screens/netmeasure/NetPathsScreen').then((m) => ({ default: m.NetPathsScreen })));
const ThreatIntelScreen = lazy(() => import('./screens/vulns/ThreatIntelScreen').then((m) => ({ default: m.ThreatIntelScreen })));
const CtiScreen = lazy(() => import('./screens/cti/CtiScreen').then((m) => ({ default: m.CtiScreen })));
const DefenderScreen = lazy(() => import('./screens/config/DefenderScreen').then((m) => ({ default: m.DefenderScreen })));
const HuntingScreen = lazy(() => import('./screens/response/HuntingScreen').then((m) => ({ default: m.HuntingScreen })));
const DiskEncryptionScreen = lazy(() => import('./screens/config/DiskEncryptionScreen').then((m) => ({ default: m.DiskEncryptionScreen })));
const DeviceControlScreen = lazy(() => import('./screens/config/DeviceControlScreen').then((m) => ({ default: m.DeviceControlScreen })));
const ResponseScreen = lazy(() => import('./screens/response/ResponseScreen').then((m) => ({ default: m.ResponseScreen })));
const ApplicationsScreen = lazy(() => import('./screens/telemetry/ApplicationsScreen').then((m) => ({ default: m.ApplicationsScreen })));
const PrivacyScreen = lazy(() => import('./screens/privacy/PrivacyScreen').then((m) => ({ default: m.PrivacyScreen })));
const CertificatesScreen = lazy(() => import('./screens/certificates/CertificatesScreen').then((m) => ({ default: m.CertificatesScreen })));
const OnCallScreen = lazy(() => import('./screens/alerts/OnCallScreen').then((m) => ({ default: m.OnCallScreen })));
const ConfigHistoryScreen = lazy(() => import('./screens/settings/ConfigHistoryScreen').then((m) => ({ default: m.ConfigHistoryScreen })));
const LifecycleScreen = lazy(() => import('./screens/lifecycle/LifecycleScreen').then((m) => ({ default: m.LifecycleScreen })));
const ContractDetail = lazy(() => import('./screens/contracts/ContractDetail').then((m) => ({ default: m.ContractDetail })));
const AiScreen = lazy(() => import('./screens/ai/AiScreen').then((m) => ({ default: m.AiScreen })));
const InsightsScreen = lazy(() => import('./screens/insights/InsightsScreen').then((m) => ({ default: m.InsightsScreen })));
const ReputationScreen = lazy(() => import('./screens/reputation/ReputationScreen').then((m) => ({ default: m.ReputationScreen })));
const SoftwareScreen = lazy(() => import('./screens/software/SoftwareScreen').then((m) => ({ default: m.SoftwareScreen })));
const CollectScreen = lazy(() => import('./screens/inventory/FieldsScreens').then((m) => ({ default: m.CollectScreen })));
const SecurityScreen = lazy(() => import('./screens/me/SecurityScreen').then((m) => ({ default: m.SecurityScreen })));
const NotificationsScreen = lazy(() => import('./screens/me/NotificationsScreen').then((m) => ({ default: m.NotificationsScreen })));
const AssetDetail = lazy(() => import('./screens/inventory/AssetDetail').then((m) => ({ default: m.AssetDetail })));
const AssetAuditScreen = lazy(() => import('./screens/inventory/AssetAudit').then((m) => ({ default: m.AssetAuditScreen })));
const AssetLinkScreen = lazy(() => import('./screens/inventory/AssetLabel').then((m) => ({ default: m.AssetLinkScreen })));
const UninstallCodeScreen = lazy(() => import('./screens/agents/UninstallCode').then((m) => ({ default: m.UninstallCodeScreen })));
const AgentsScreen = lazy(() => import('./screens/agents/AgentsScreen').then((m) => ({ default: m.AgentsScreen })));
const AgentWizard = lazy(() => import('./screens/agents/AgentWizard').then((m) => ({ default: m.AgentWizard })));
const DirectoryScreen = lazy(() => import('./screens/directory/DirectoryScreen').then((m) => ({ default: m.DirectoryScreen })));
const ConnectDirectory = lazy(() => import('./screens/directory/ConnectDirectory').then((m) => ({ default: m.ConnectDirectory })));
const TagsScreen = lazy(() => import('./screens/tags/TagsScreen').then((m) => ({ default: m.TagsScreen })));
const GroupEditor = lazy(() => import('./screens/tags/GroupEditor').then((m) => ({ default: m.GroupEditor })));
const AvailabilityScreen = lazy(() => import('./screens/alerts/ServicesScreen').then((m) => ({ default: m.AvailabilityScreen })));
const ServiceScreen = lazy(() => import('./screens/alerts/ServicesScreen').then((m) => ({ default: m.ServiceScreen })));
const SlaMatrixScreen = lazy(() => import('./screens/sla/SlaMatrixScreen').then((m) => ({ default: m.SlaMatrixScreen })));
const BehaviorScreen = lazy(() => import('./screens/behavior/BehaviorScreen').then((m) => ({ default: m.BehaviorScreen })));
const RootCausesScreen = lazy(() => import('./screens/sla/RootCausesScreen').then((m) => ({ default: m.RootCausesScreen })));
const AlertsScreen = lazy(() => import('./screens/alerts/AlertsScreen').then((m) => ({ default: m.AlertsScreen })));
const RulesScreen = lazy(() => import('./screens/alerts/RulesScreen').then((m) => ({ default: m.RulesScreen })));
const TemplateEditor = lazy(() => import('./screens/alerts/TemplatesScreen').then((m) => ({ default: m.TemplateEditor })));
const RuleWizard = lazy(() => import('./screens/alerts/RuleWizard').then((m) => ({ default: m.RuleWizard })));
const ChannelsScreen = lazy(() => import('./screens/alerts/ChannelsScreen').then((m) => ({ default: m.ChannelsScreen })));
const ItsmScreen = lazy(() => import('./screens/itsm/ItsmScreen').then((m) => ({ default: m.ItsmScreen })));
const RemoteScreen = lazy(() => import('./screens/remote/RemoteScreen').then((m) => ({ default: m.RemoteScreen })));
const NewRemoteSession = lazy(() => import('./screens/remote/RemoteScreen').then((m) => ({ default: m.NewRemoteSession })));
const RemoteSessionScreen = lazy(() => import('./screens/remote/RemoteSession').then((m) => ({ default: m.RemoteSessionScreen })));
const ScriptsScreen = lazy(() => import('./screens/config/Scripts').then((m) => ({ default: m.ScriptsScreen })));
const SigningScreen = lazy(() => import('./screens/config/Signing').then((m) => ({ default: m.SigningScreen })));
const ScriptScreen = lazy(() => import('./screens/config/Scripts').then((m) => ({ default: m.ScriptScreen })));
const PatchesScreen = lazy(() => import('./screens/inventory/Patches').then((m) => ({ default: m.PatchesScreen })));
const MobileScreen = lazy(() => import('./screens/inventory/Mobile').then((m) => ({ default: m.MobileScreen })));
const MdmScreen = lazy(() => import('./screens/mdm/MdmScreen').then((m) => ({ default: m.MdmScreen })));
const MdmSettingsScreen = lazy(() => import('./screens/mdm/MdmSettingsScreen').then((m) => ({ default: m.MdmSettingsScreen })));
const PlaybooksScreen = lazy(() => import('./screens/config/Playbooks').then((m) => ({ default: m.PlaybooksScreen })));
const PlaybookScreen = lazy(() => import('./screens/config/Playbooks').then((m) => ({ default: m.PlaybookScreen })));
const KiosksScreen = lazy(() => import('./screens/kiosk/KiosksScreen').then((m) => ({ default: m.KiosksScreen })));
const WorkflowsScreen = lazy(() => import('./screens/workflows/WorkflowsScreen').then((m) => ({ default: m.WorkflowsScreen })));
const WorkflowEditor = lazy(() => import('./screens/workflows/WorkflowEditor').then((m) => ({ default: m.WorkflowEditor })));
const WorkflowRunScreen = lazy(() => import('./screens/workflows/WorkflowRun').then((m) => ({ default: m.WorkflowRunScreen })));
const ConfigProfilesScreen = lazy(() => import('./screens/config/ConfigProfiles').then((m) => ({ default: m.ConfigProfilesScreen })));
const ConfigProfileScreen = lazy(() => import('./screens/config/ConfigProfiles').then((m) => ({ default: m.ConfigProfileScreen })));
const DeploymentsScreen = lazy(() => import('./screens/deploy/Deployments').then((m) => ({ default: m.DeploymentsScreen })));
const NewDeployment = lazy(() => import('./screens/deploy/Deployments').then((m) => ({ default: m.NewDeployment })));
const DeploymentDetail = lazy(() => import('./screens/deploy/Deployments').then((m) => ({ default: m.DeploymentDetail })));
const SitesScreen = lazy(() => import('./screens/deploy/Deployments').then((m) => ({ default: m.SitesScreen })));
const StoreScreen = lazy(() => import('./screens/store/StoreScreen').then((m) => ({ default: m.StoreScreen })));
const StoreApprovals = lazy(() => import('./screens/store/StoreApprovals').then((m) => ({ default: m.StoreApprovals })));
const StoreAdmin = lazy(() => import('./screens/store/StoreAdmin').then((m) => ({ default: m.StoreAdmin })));
const NocScreen = lazy(() => import('./screens/noc/NocScreen').then((m) => ({ default: m.NocScreen })));
const MaintenanceWindowsScreen = lazy(() => import('./screens/alerts/MaintenanceWindowsScreen').then((m) => ({ default: m.MaintenanceWindowsScreen })));
const ServiceDeskScreen = lazy(() => import('./screens/servicedesk/ServiceDeskScreen').then((m) => ({ default: m.ServiceDeskScreen })));
const SocScreen = lazy(() => import('./screens/soc/SocScreen').then((m) => ({ default: m.SocScreen })));
const TemplatesScreen = lazy(() => import('./screens/templates/TemplatesScreen').then((m) => ({ default: m.TemplatesScreen })));
const VoiceAnswer = lazy(() => import('./screens/remote/Voice').then((m) => ({ default: m.VoiceAnswer })));
const PublicTerm = lazy(() => import('./screens/lifecycle/Ham').then((m) => ({ default: m.PublicTerm })));
const SurveyPage = lazy(() => import('./screens/sentiment/SurveyForm').then((m) => ({ default: m.SurveyPage })));
const SentimentScreen = lazy(() => import('./screens/sentiment/SentimentScreen').then((m) => ({ default: m.SentimentScreen })));
const DeviceStore = lazy(() => import('./screens/store/DeviceStore').then((m) => ({ default: m.DeviceStore })));
const MobileEnroll = lazy(() => import('./screens/inventory/MobileEnroll').then((m) => ({ default: m.MobileEnroll })));
const StoreCatalog = lazy(() => import('./screens/store/StoreAdmin').then((m) => ({ default: m.StoreCatalog })));
const ThirdPartyCatalogScreen = lazy(() => import('./screens/store/ThirdPartyCatalog').then((m) => ({ default: m.ThirdPartyCatalogScreen })));
const PackageDetail = lazy(() => import('./screens/store/StoreAdmin').then((m) => ({ default: m.PackageDetail })));
const IntegrityScreen = lazy(() => import('./screens/integrity/IntegrityScreen').then((m) => ({ default: m.IntegrityScreen })));
const PolicyWizard = lazy(() => import('./screens/integrity/PolicyWizard').then((m) => ({ default: m.PolicyWizard })));
const CredentialVaultScreen = lazy(() => import('./screens/discovery/DiscoveryScreen').then((m) => ({ default: m.CredentialVaultScreen })));
const DiscoveryScreen = lazy(() => import('./screens/discovery/DiscoveryScreen').then((m) => ({ default: m.DiscoveryScreen })));
const ScanWizard = lazy(() => import('./screens/discovery/ScanWizard').then((m) => ({ default: m.ScanWizard })));
const RunView = lazy(() => import('./screens/discovery/RunView').then((m) => ({ default: m.RunView })));
const ReportsScreen = lazy(() => import('./screens/reports/ReportsScreen').then((m) => ({ default: m.ReportsScreen })));
const QueryBuilder = lazy(() => import('./screens/reports/QueryBuilder').then((m) => ({ default: m.QueryBuilder })));
const DashboardsScreen = lazy(() => import('./screens/reports/DashboardsScreen').then((m) => ({ default: m.DashboardsScreen })));
const DashboardView = lazy(() => import('./screens/reports/DashboardsScreen').then((m) => ({ default: m.DashboardView })));
const AssetTypesScreen = lazy(() => import('./screens/inventory/AssetTypesScreen').then((m) => ({ default: m.AssetTypesScreen })));
const LifecycleFlowScreen = lazy(() => import('./screens/inventory/LifecycleFlowScreen').then((m) => ({ default: m.LifecycleFlowScreen })));
const MapBuilderScreen = lazy(() => import('./screens/servicemap/MapBuilderScreen').then((m) => ({ default: m.MapBuilderScreen })));
const LogicalMapScreen = lazy(() => import('./screens/servicemap/LogicalMapScreen').then((m) => ({ default: m.LogicalMapScreen })));
const ApprovalsScreen = lazy(() => import('./screens/deploy/ApprovalsScreen').then((m) => ({ default: m.ApprovalsScreen })));
const ReadyDashboardView = lazy(() => import('./screens/reports/DashboardsScreen').then((m) => ({ default: m.ReadyDashboardView })));
const ItsmConnectionScreen = lazy(() => import('./screens/itsm/ItsmConnection').then((m) => ({ default: m.ItsmConnectionScreen })));
const AutomationScreen = lazy(() => import('./screens/itsm/AutomationScreen').then((m) => ({ default: m.AutomationScreen })));
const AutomationWizard = lazy(() => import('./screens/itsm/AutomationScreen').then((m) => ({ default: m.AutomationWizard })));

export function App() {
  const [lang, setLang] = useState<Lang>(() => resolveLang({ user: rememberedLang(), browser: navigator.languages }));
  const [dicts, setDicts] = useState(platformMessages);
  const [full, setFull] = useState(false);
  // A entrada abre com os textos da casca (pacote principal); os das telas chegam em paralelo e só a sessão aberta
  // espera por eles. Se o arquivo falhar, a console segue com os da casca.
  useEffect(() => {
    let alive = true;
    loadPlatformMessages()
      .then((d) => alive && setDicts(d))
      .catch(() => undefined)
      .finally(() => alive && setFull(true));
    return () => {
      alive = false;
    };
  }, []);
  return (
    <I18nProvider lang={lang} dicts={dicts}>
      <ToastProvider><Boot lang={lang} setLang={setLang} full={full} /></ToastProvider>
    </I18nProvider>
  );
}

/** O retorno do OIDC só pode ser trocado uma vez (o StrictMode do React roda o efeito duas vezes em dev). */
let callback: Promise<AuthAnswer> | null = null;
function ssoCallbackOnce(auth: TenantAuth): Promise<AuthAnswer> {
  callback ??= auth.ssoCallback();
  return callback;
}

/** Uma autenticação por aba: o token de acesso fica só na memória dela. */
let tenantAuth: TenantAuth | null = null;

type Phase =
  | { kind: 'loading' }
  | { kind: 'no-tenant' }
  | { kind: 'not-found' }
  | { kind: 'error'; error: unknown }
  | { kind: 'signin'; tenant: SignIn; auth: TenantAuth; problem?: Problem | null; pending?: AuthAnswer | null }
  | { kind: 'ready'; tenant: SignIn; auth: TenantAuth; me: Me }
  | { kind: 'device-store'; token: string }
  | { kind: 'mobile-enroll'; slug: string; token: string }
  | { kind: 'term'; token: string }
  | { kind: 'voice'; token: string }
  | { kind: 'survey'; token: string }
  /** R01-11: volta de um teste de login em nova aba — sem sessão; o resultado aparece na aba da configuração. */
  | { kind: 'probe'; ok: boolean };

/**
 * Abertura da plataforma: tenant pelo endereço → dados públicos de entrada (marca, idioma,
 * métodos) → retorno do OIDC, se for o caso → /me. Sem sessão, mostra a entrada (T-01).
 */
function Boot({ lang, setLang, full }: { lang: Lang; setLang: (l: Lang) => void; full: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [boot, setBoot] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const slug = currentSlug();
      if (!slug) return setPhase({ kind: 'no-tenant' });
      const anon = createHiveClient({ baseUrl: apiBase(), getToken: () => null });
      let tenant: SignIn;
      try {
        tenant = await unwrap(anon.GET('/api/v1/public/tenants/{slug}', { params: { path: { slug } } }));
      } catch (e) {
        if (!alive) return;
        return setPhase(isApiError(e) && e.status === 404 ? { kind: 'not-found' } : { kind: 'error', error: e });
      }
      applyBranding(tenant.branding, tenant.slug);
      if (!rememberedLang()) setLang(resolveLang({ tenant: tenant.defaultLang, browser: navigator.languages }));
      tenantAuth ??= createTenantAuth(slug, apiBase());
      const auth = tenantAuth;
      const path = appPath();
      // Loja aberta pelo atalho da máquina: só o link, sem login
      // Hive Mobile: o celular aberto pelo QR de "Celulares e tablets" se cadastra só com o link
      const me = /^\/m\/enroll\/([A-Za-z0-9_-]{40,60})$/.exec(path);
      if (me) return alive && setPhase({ kind: 'mobile-enroll', slug, token: me[1] ?? '' });
      // celular já cadastrado: cada abertura do Hive Mobile atualiza o que o aparelho informa
      void import('./screens/inventory/MobileEnroll').then((m) => m.syncThisPhone(slug));
      const ds = /^\/my-store\/([A-Za-z0-9_-]{40,60})$/.exec(path);
      if (ds) return alive && setPhase({ kind: 'device-store', token: ds[1] ?? '' });
      // G8: pesquisa de sentimento aberta pelo aviso do agente (ou no celular): só o link
      const sv = /^\/my-survey\/([A-Za-z0-9_-]{40,60})$/.exec(path);
      if (sv) return alive && setPhase({ kind: 'survey', token: sv[1] ?? '' });
      // termo de responsabilidade: a pessoa aceita só com o link
      const tm = /^\/term\/([A-Za-z0-9_-]{40,60})$/.exec(path);
      if (tm) return alive && setPhase({ kind: 'term', token: tm[1] ?? '' });
      // chamada de voz do técnico: o agente abre o link no computador do usuário
      const vc = /^\/voice\/([A-Za-z0-9_-]{40,60})$/.exec(path);
      if (vc) return alive && setPhase({ kind: 'voice', token: vc[1] ?? '' });
      if (path === '/auth/invite' || path === '/auth/reset') return alive && setPhase({ kind: 'signin', tenant, auth });
      if (path === '/auth/saml') {
        // volta do SAML: o núcleo já abriu a sessão (cookie) ou mandou o próximo passo/erro no fragmento
        const f = new URLSearchParams(window.location.hash.slice(1));
        window.history.replaceState(null, '', withBase('/'));
        if (f.get('probe')) return alive && setPhase({ kind: 'probe', ok: true });
        if (f.get('error')) return alive && setPhase({ kind: 'signin', tenant, auth, problem: { code: 'callback', title: f.get('error') ?? '' } });
        const step = f.get('step');
        if (step) {
          try {
            const a = JSON.parse(atob(step.replace(/-/g, '+').replace(/_/g, '/'))) as AuthAnswer;
            return alive && setPhase({ kind: 'signin', tenant, auth, pending: a });
          } catch {
            /* passo ilegível: segue para a entrada normal */
          }
        }
      }
      if (path === '/auth/callback') {
        try {
          const a = await ssoCallbackOnce(auth);
          if (a.status === 'probe') return alive && setPhase({ kind: 'probe', ok: true });
          // R01-17 B: o perfil pede MFA e o provedor não provou — volta ao provedor pedindo o nível maior
          if (a.status === 'step-up' && a.url && /^https?:\/\//.test(a.url)) return void window.location.assign(a.url);
          if (a.status !== 'ok') return alive && setPhase({ kind: 'signin', tenant, auth, pending: a });
          navigate(auth.returnTo(), { replace: true });
        } catch (e) {
          if (!alive) return;
          // resposta do núcleo (ex.: "Você ainda não tem acesso ao Hive"): mostra o motivo real, não o texto genérico da volta
          if (e instanceof AuthError && e.status >= 400 && e.status < 500 && (e.problem.title || e.problem.detail)) {
            return setPhase({ kind: 'signin', tenant, auth, problem: { ...e.problem, status: e.status } as Problem });
          }
          const title = e instanceof AuthError ? String(e.problem.title ?? e.message) : e instanceof Error ? e.message : String(e);
          return setPhase({ kind: 'signin', tenant, auth, problem: { code: 'callback', title } });
        }
      }
      const token = await auth.getToken();
      if (!token) return alive && setPhase({ kind: 'signin', tenant, auth });
      const api = createHiveClient({ baseUrl: apiBase(), getToken: () => auth.getToken(), tenant: slug });
      try {
        let me = await unwrap(api.GET('/api/v1/me'));
        if (!alive) return;
        // o idioma salvo no perfil vence; sem ele, vale o último escolhido neste navegador (na entrada, por exemplo),
        // que passa a ser o do perfil
        const picked = me.kind === 'user' && me.langSaved === false ? rememberedLang() : null;
        const l = resolveLang({ user: picked ?? me.lang, tenant: tenant.defaultLang, browser: navigator.languages });
        if (picked) me = await unwrap(api.PUT('/api/v1/me/preferences', { body: { lang: picked } })).catch(() => me);
        if (!alive) return;
        setLang(l);
        rememberLang(l);
        applyTheme(themeOf(me));
        applyBranding(me.tenant?.branding ?? tenant.branding, tenant.slug);
        setPhase({ kind: 'ready', tenant, auth, me });
      } catch (e) {
        if (!alive) return;
        if (isApiError(e) && e.status === 401) {
          await auth.forget();
          return setPhase({ kind: 'signin', tenant, auth, problem: e.problem });
        }
        setPhase({ kind: 'error', error: e });
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- na abertura e depois de entrar
  }, [boot]);

  const onLang = useCallback((l: Lang) => setLang(l), [setLang]);

  if (phase.kind === 'loading') return <Centered><Loading /></Centered>;
  if (phase.kind === 'no-tenant')
    return (
      <Centered>
        <h1>{t('boot.noTenant.title')}</h1>
        <p className="muted">{t('boot.noTenant.text')}</p>
      </Centered>
    );
  if (phase.kind === 'not-found')
    return (
      <Centered>
        <h1>{t('boot.notFound.title')}</h1>
        <p className="muted">{t('boot.notFound.text')}</p>
      </Centered>
    );
  if (phase.kind === 'error') return <BootError error={phase.error} />;
  // daqui em diante as telas usam os textos carregados à parte
  if (!full && phase.kind !== 'signin') return <Centered><Loading /></Centered>;
  if (phase.kind === 'probe')
    return (
      <Centered>
        <h1>{t('authx.probe.title')}</h1>
        <p className="muted">{t('authx.probe.text')}</p>
        <button type="button" className="btn btn-primary" onClick={() => window.close()}>
          {t('authx.probe.close')}
        </button>
      </Centered>
    );
  if (phase.kind === 'voice')
    return (
      <Suspense fallback={<Loading />}>
        <VoiceAnswer token={phase.token} />
      </Suspense>
    );
  if (phase.kind === 'term')
    return (
      <Suspense fallback={<Loading />}>
        <PublicTerm token={phase.token} />
      </Suspense>
    );
  if (phase.kind === 'survey')
    return (
      <Suspense fallback={<Loading />}>
        <SurveyPage token={phase.token} />
      </Suspense>
    );
  if (phase.kind === 'mobile-enroll')
    return (
      <Suspense fallback={<Loading />}>
        <MobileEnroll slug={phase.slug} token={phase.token} />
      </Suspense>
    );
  if (phase.kind === 'device-store')
    return (
      <Suspense fallback={<Loading />}>
        <DeviceStore token={phase.token} />
      </Suspense>
    );
  if (phase.kind === 'signin')
    return (
      <SignInScreen
        tenant={phase.tenant}
        auth={phase.auth}
        problem={phase.problem ?? null}
        pending={phase.pending ?? null}
        returnTo={location.pathname.startsWith('/auth/') ? '/' : location.pathname + location.search}
        onLang={onLang}
        onDone={() => {
          const back = phase.auth.returnTo();
          navigate(back === '/' && !location.pathname.startsWith('/auth/') ? location.pathname + location.search : back, { replace: true });
          setPhase({ kind: 'loading' });
          setBoot((b) => b + 1);
        }}
      />
    );
  return (
    <SessionProvider tenant={phase.tenant} auth={phase.auth} initialMe={phase.me} lang={lang} onLang={onLang}>
      <Ready />
    </SessionProvider>
  );
}

function Ready() {
  const s = useSession();
  const navigate = useNavigate();
  const source = useMemo(() => createHelpSource(s.api), [s.api]);
  // a licença é lida junto com o catálogo de telas: a faixa já está decidida quando a tela abre (sem empurrar o conteúdo)
  useMemo(() => prefetchLicense(s.api), [s.api]);
  const openManual = useCallback((id: string, anchor?: string) => navigate(`/help/${id}${anchor ? `#${anchor}` : ''}`), [navigate]);
  // todo campo de tags (assistentes e telas) sugere as tags que já existem enquanto se digita
  const suggestTags = useCallback(async (q: string): Promise<TagSuggestion[]> => {
    if (!s.can('assets:read')) return [];
    const r = await unwrap(s.api.GET('/api/v1/tags/suggest', { params: { query: { q } } }));
    return (r.existing ?? []).map((x) => ({ tag: x.tag ?? '', assets: x.assets ?? 0 })).filter((x) => x.tag);
  }, [s]);
  return (
    <TagSuggestContext.Provider value={suggestTags}>
    <HelpProvider source={source} onOpenWizard={(id) => navigate(wizardRoute(id))} onOpenManual={openManual}>
      <Shell>
        <LicenseBanner>
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/auth/callback" element={<Navigate to="/" replace />} />
          <Route path="/assets" element={<AssetsScreen />} />
          <Route path="/remote" element={<RemoteScreen />} />
          <Route path="/remote/new" element={<NewRemoteSession />} />
          <Route path="/remote/:id" element={<RemoteSessionScreen />} />
          <Route path="/playbooks" element={<PlaybooksScreen />} />
          <Route path="/mobile" element={<MobileScreen />} />
          <Route path="/mdm" element={<MdmScreen />} />
          <Route path="/mdm/settings" element={<MdmSettingsScreen />} />
          <Route path="/patches" element={<PatchesScreen />} />
          <Route path="/scripts" element={<ScriptsScreen />} />
          <Route path="/scripts/:id" element={<ScriptScreen />} />
          <Route path="/signing" element={<SigningScreen />} />
          <Route path="/playbooks/:id" element={<PlaybookScreen />} />
          <Route path="/workflows" element={<WorkflowsScreen />} />
          <Route path="/workflows/new" element={<WorkflowEditor />} />
          <Route path="/workflows/runs/:id" element={<WorkflowRunScreen />} />
          <Route path="/workflows/:id" element={<WorkflowEditor />} />
          <Route path="/config-profiles" element={<ConfigProfilesScreen />} />
          <Route path="/kiosks" element={<KiosksScreen />} />
          <Route path="/config-profiles/new" element={<ConfigProfileScreen />} />
          <Route path="/config-profiles/:id" element={<ConfigProfileScreen />} />
          <Route path="/deployments" element={<DeploymentsScreen />} />
          <Route path="/deployments/new" element={<NewDeployment />} />
          <Route path="/deployments/sites" element={<SitesScreen />} />
          <Route path="/deployments/:id" element={<DeploymentDetail />} />
          <Route path="/store" element={<StoreScreen />} />
          <Route path="/store/approvals" element={<StoreApprovals />} />
          <Route path="/approvals" element={<ApprovalsScreen />} />
          <Route path="/topology/logical" element={<LogicalMapScreen />} />
          <Route path="/maps/builder" element={<MapBuilderScreen />} />
          <Route path="/maps/builder/:id" element={<MapBuilderScreen />} />
          <Route path="/store/admin" element={<StoreAdmin />} />
          <Route path="/store/catalog" element={<StoreCatalog />} />
          <Route path="/store/third-party" element={<ThirdPartyCatalogScreen />} />
          <Route path="/store/admin/packages/:id" element={<PackageDetail />} />
          <Route path="/integrity" element={<IntegrityScreen />} />
          <Route path="/integrity/policies/:id" element={<PolicyWizard />} />
          <Route path="/discovery" element={<DiscoveryScreen />} />
          <Route path="/credentials" element={<CredentialVaultScreen />} />
          <Route path="/discovery/new" element={<ScanWizard />} />
          <Route path="/discovery/scans/:id/edit" element={<ScanWizard />} />
          <Route path="/discovery/runs/:id" element={<RunView />} />
          <Route path="/reports" element={<ReportsScreen />} />
          <Route path="/reports/new" element={<QueryBuilder />} />
          <Route path="/reports/q/:id" element={<QueryBuilder />} />
          <Route path="/dashboards" element={<DashboardsScreen />} />
          <Route path="/dashboards/ready/:key" element={<ReadyDashboardView />} />
          <Route path="/dashboards/:id" element={<DashboardView />} />
          <Route path="/assets/fields" element={<CustomFieldsScreen />} />
          <Route path="/assets/types" element={<AssetTypesScreen />} />
          <Route path="/assets/lifecycle-flow" element={<LifecycleFlowScreen />} />
          <Route path="/assets/collect" element={<CollectScreen />} />
          <Route path="/assets/security" element={<SecurityOverview />} />
          <Route path="/assets/audit" element={<AssetAuditScreen />} />
          <Route path="/assets/:id" element={<AssetDetail />} />
          {/* link do QR da etiqueta (R11-03): sem login, a entrada volta para cá e a ficha abre */}
          <Route path="/a/:code" element={<AssetLinkScreen />} />
          <Route path="/vulnerabilities" element={<VulnScreen />} />
          <Route path="/attack-paths" element={<AttackPathsScreen />} />
          <Route path="/vulnerabilities/settings" element={<RiskSettings />} />
          <Route path="/vulnerabilities/cve/:id" element={<CveDetail />} />
          <Route path="/vulnerabilities/:tab" element={<VulnScreen />} />
          <Route path="/templates" element={<TemplatesScreen />} />
          <Route path="/noc" element={<NocScreen />} />
          <Route path="/maintenance-windows" element={<MaintenanceWindowsScreen />} />
          <Route path="/soc" element={<SocScreen />} />
          <Route path="/service-desk" element={<ServiceDeskScreen />} />
          <Route path="/licenses" element={<LicensesScreen />} />
          <Route path="/licenses/:tab" element={<LicensesScreen />} />
          <Route path="/licenses/product/:key" element={<ProductOverview />} />
          <Route path="/reputation" element={<ReputationScreen />} />
          <Route path="/code" element={<CodeScreen />} />
          <Route path="/external-surface" element={<SurfaceScreen />} />
          <Route path="/insights" element={<InsightsScreen />} />
          <Route path="/contracts" element={<ContractsScreen />} />
          <Route path="/contracts/tab/:tab" element={<ContractsScreen />} />
          <Route path="/contracts/new" element={<ContractWizard />} />
          <Route path="/contracts/:id" element={<ContractDetail />} />
          <Route path="/decisions" element={<DecisionsScreen />} />
          <Route path="/response" element={<ResponseScreen />} />
          <Route path="/app-control" element={<AppControlScreen />} />
          <Route path="/ioc" element={<IocScreen />} />
          <Route path="/threat-intel" element={<ThreatIntelScreen />} />
          <Route path="/cti" element={<CtiScreen />} />
          <Route path="/device-control" element={<DeviceControlScreen />} />
          <Route path="/disk-encryption" element={<DiskEncryptionScreen />} />
          <Route path="/hunting" element={<HuntingScreen />} />
          <Route path="/defender" element={<DefenderScreen />} />
          <Route path="/privileges" element={<PrivilegesScreen />} />
          <Route path="/network" element={<NetworkScreen />} />
          <Route path="/firewalls-wifi" element={<FirewallsWifiScreen />} />
          <Route path="/config-backups" element={<ConfigBackupsScreen />} />
          <Route path="/kubernetes" element={<KubernetesScreen />} />
          <Route path="/graph" element={<GraphScreen />} />
          <Route path="/maps/geo" element={<GeoScreen />} />
          <Route path="/identities" element={<IdentitiesScreen />} />
          <Route path="/access-reviews" element={<AccessReviewsScreen />} />
          <Route path="/saas" element={<SaasScreen />} />
          <Route path="/virtualization" element={<VirtualizationScreen />} />
          <Route path="/ot" element={<OtScreen />} />
          <Route path="/ot/:tab" element={<OtScreen />} />
          <Route path="/cloud" element={<CloudScreen />} />
          <Route path="/cloud-posture" element={<CloudPostureScreen />} />
          <Route path="/cloud-posture/:tab" element={<CloudPostureScreen />} />
          <Route path="/rightsizing" element={<RightsizingScreen />} />
          <Route path="/snmp-traps" element={<SnmpTrapsScreen />} />
          <Route path="/service-map" element={<ServiceMapScreen />} />
          <Route path="/org" element={<OrgScreen />} />
          <Route path="/org/:tab" element={<OrgScreen />} />
          <Route path="/cloud/:tab" element={<CloudScreen />} />
          <Route path="/cloud/clusters/:clusterId" element={<CloudScreen />} />
          <Route path="/cloud/accounts/:id" element={<CloudScreen />} />
          <Route path="/cloud/accounts/:id/:tab" element={<CloudScreen />} />
          <Route path="/cloud/accounts/:id/clusters/:clusterId" element={<CloudScreen />} />
          <Route path="/virtualization/:tab" element={<VirtualizationScreen />} />
          <Route path="/apis" element={<ApisScreen />} />
          <Route path="/apis/:id" element={<ApiScreen />} />
          <Route path="/kubernetes/:id" element={<ClusterScreen />} />
          <Route path="/kubernetes/:id/:tab" element={<ClusterScreen />} />
          <Route path="/compliance" element={<ComplianceScreen />} />
          <Route path="/experience" element={<ExperienceScreen />} />
          <Route path="/sentiment" element={<SentimentScreen />} />
          <Route path="/compliance/:tab" element={<ComplianceScreen />} />
          <Route path="/network/:tab" element={<NetworkScreen />} />
          <Route path="/privileges/:tab" element={<PrivilegesScreen />} />
          <Route path="/decisions/:tab" element={<DecisionsScreen />} />
          <Route path="/certificates" element={<CertificatesScreen />} />
          <Route path="/privacy" element={<PrivacyScreen />} />
          <Route path="/privacy/:tab" element={<PrivacyScreen />} />
          <Route path="/applications" element={<ApplicationsScreen />} />
          <Route path="/performance" element={<PerformanceScreen />} />
          <Route path="/network-measurement" element={<NetOverviewScreen />} />
          <Route path="/network-measurement/links" element={<NetLinksScreen />} />
          <Route path="/network-measurement/transactions" element={<NetTransactionsScreen />} />
          <Route path="/network-measurement/flows" element={<NetFlowsScreen />} />
          <Route path="/network-measurement/paths" element={<NetPathsScreen />} />
          <Route path="/network-measurement/calls" element={<NetCallsScreen />} />
          <Route path="/network-measurement/rum" element={<NetRumScreen />} />
          <Route path="/network-measurement/packets" element={<NetPacketsScreen />} />
          <Route path="/network-measurement/synthetic" element={<NetSyntheticScreen />} />
          <Route path="/network-measurement/app-experience" element={<NetAppExpScreen />} />
          <Route path="/network-measurement/acceleration" element={<NetAccelScreen />} />
          <Route path="/network-measurement/investigations" element={<NetInvestigationsScreen />} />
          <Route path="/network-measurement/config" element={<NetConfigScreen />} />
          <Route path="/lifecycle/certificates" element={<Navigate to="/certificates" replace />} />
          <Route path="/lifecycle/deadlines" element={<Navigate to="/contracts/tab/coverage" replace />} />
          <Route path="/lifecycle" element={<LifecycleScreen />} />
          <Route path="/lifecycle/:tab" element={<LifecycleScreen />} />
          <Route path="/ai" element={<AiScreen />} />
          <Route path="/ai/:tab" element={<AiScreen />} />
          <Route path="/software" element={<SoftwareScreen />} />
          <Route path="/software/:tab" element={<SoftwareScreen />} />
          <Route path="/agents" element={<AgentsScreen />} />
          <Route path="/uninstall-code" element={<UninstallCodeScreen />} />
          <Route path="/agents/uninstall-code" element={<Navigate to="/uninstall-code" replace />} />
          <Route path="/agents/new" element={<AgentWizard kind="agent" />} />
          <Route path="/agents/collector" element={<AgentWizard kind="collector" />} />
          <Route path="/agents/:tab" element={<AgentsScreen />} />
          <Route path="/directory" element={<DirectoryScreen />} />
          <Route path="/directory/connect" element={<ConnectDirectory />} />
          <Route path="/directory/connect/:id" element={<ConnectDirectory />} />
          <Route path="/directory/:tab" element={<DirectoryScreen />} />
          <Route path="/tags" element={<TagsScreen />} />
          <Route path="/tags/groups/new" element={<GroupEditor />} />
          <Route path="/tags/groups/:id" element={<GroupEditor />} />
          <Route path="/tags/:tab" element={<TagsScreen />} />
          <Route path="/alerts" element={<AlertsScreen />} />
          <Route path="/availability" element={<AvailabilityScreen />} />
          <Route path="/sla-matrix" element={<SlaMatrixScreen />} />
          <Route path="/root-causes" element={<RootCausesScreen />} />
          <Route path="/behavior" element={<BehaviorScreen />} />
          <Route path="/services/:id" element={<ServiceScreen />} />
          <Route path="/alerts/known-errors" element={<KnownErrorsScreen />} />
          <Route path="/alerts/rules" element={<RulesScreen />} />
          <Route path="/alerts/rules/tab/:tab" element={<RulesScreen />} />
          <Route path="/alerts/rules/new" element={<RuleWizard />} />
          <Route path="/alerts/rules/:id" element={<RuleWizard />} />
          <Route path="/alerts/channels" element={<ChannelsScreen />} />
          <Route path="/alerts/oncall" element={<OnCallScreen />} />
          <Route path="/alerts/templates/new" element={<TemplateEditor />} />
          <Route path="/alerts/templates/:id" element={<TemplateEditor />} />
          <Route path="/alerts/:alertId" element={<AlertLink />} />
          <Route path="/me/notifications" element={<NotificationsScreen />} />
          <Route path="/me/security" element={<SecurityScreen />} />
          <Route path="/itsm" element={<ItsmScreen />} />
          <Route path="/itsm/connection/:id" element={<ItsmConnectionScreen />} />
          <Route path="/itsm/:tab" element={<ItsmScreen />} />
          <Route path="/automation" element={<AutomationScreen />} />
          <Route path="/automation/new" element={<AutomationWizard />} />
          <Route path="/automation/:id" element={<AutomationWizard />} />
          <Route path="/users" element={<UsersScreen />} />
          <Route path="/users/:tab" element={<UsersScreen />} />
          <Route path="/users/profiles/new" element={<ProfileEditor />} />
          <Route path="/users/profiles/:id/edit" element={<ProfileEditor />} />
          <Route path="/integrations" element={<IntegrationsScreen />} />
          <Route path="/security-connectors" element={<SecurityConnectorsScreen />} />
          <Route path="/security-connectors/:tab" element={<SecurityConnectorsScreen />} />
          <Route path="/integrations/api/docs" element={<ApiDocsScreen />} />
          <Route path="/integrations/:tab" element={<IntegrationsScreen />} />
          <Route path="/integrations/connect/:catalogId" element={<ConnectScreen />} />
          <Route path="/integrations/connections/:id" element={<ConnectionDetail />} />
          <Route path="/audit" element={<AuditScreen />} />
          <Route path="/config-history" element={<ConfigHistoryScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/settings/license" element={<LicenseScreen />} />
          <Route path="/settings/health" element={<InstallationHealthScreen />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/:article" element={<HelpCenter />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </LicenseBanner>
      </Shell>
    </HelpProvider>
    </TagSuggestContext.Provider>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="login-main" id="main" style={{ minHeight: '100vh' }}>
      <div className="login-card">{children}</div>
    </main>
  );
}

function BootError({ error }: { error: unknown }) {
  const t = useT();
  const i18n = useI18nP();
  return (
    <Centered>
      <h1>{t('boot.error.title')}</h1>
      <ErrorBox e={explainProblem(error, i18n)} actions={<button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>{t('ui.retry')}</button>} />
      <Note>{t('boot.error.note')}</Note>
    </Centered>
  );
}

export type { PlatformKey };

/** Link das notificações push (`/alerts/{id}`): abre a fila de alertas com o alerta selecionado. */
function AlertLink() {
  const { alertId } = useParams();
  return <Navigate to={`/alerts?id=${encodeURIComponent(alertId ?? '')}`} replace />;
}
